import React from 'react'
import { Box, Typography, Checkbox, Tooltip } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import MeterInfoIcon from '@/assets/meter_info_icon.svg'

interface SystemColumn {
  id: string
  label: string
}

const KEY_CANDIDATES: SystemColumn[] = [
  { id: 'water_meter', label: 'מספר כרטיס מים' },
  { id: 'internal_meter', label: 'מספר מד פנימי' },
]

interface Step3KeyColumnSelectionProps {
  selectedKeyColumns: string[]
  onKeyColumnToggle: (id: string) => void
  mappings: Record<string, string>
}

export const Step3KeyColumnSelection: React.FC<Step3KeyColumnSelectionProps> = ({
  selectedKeyColumns,
  onKeyColumnToggle,
  mappings
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const TEXT_PRIMARY = isDarkMode ? '#F8FAFC' : '#003F61'
  const TEXT_SECONDARY = isDarkMode ? '#94A3B8' : '#64748B'

  const mappedKeys = KEY_CANDIDATES.filter(col => mappings[col.id])
  const isOnlyOneMapped = mappedKeys.length === 1
  
  const missingColumnLabel = mappedKeys.length === 1 
    ? KEY_CANDIDATES.find(c => !mappings[c.id])?.label 
    : null
  const activeColumnLabel = mappedKeys.length === 1 ? mappedKeys[0].label : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, pt: 2, alignItems: 'center' }}>
      {/* Right-aligned instruction */}
      <Box sx={{ textAlign: 'right', width: '100%', px: 1 }}>
        <Typography 
          sx={{ 
            color: TEXT_SECONDARY, 
            fontSize: '15px', 
            lineHeight: 1.6, 
            fontFamily: 'Rubik',
            direction: 'rtl'
          }}
        >
          בחירת עמודת מפתח תשפיע על העמודה שלפיה יתעדכנו הנתונים במערכת.
          <br />
ניתן לבחור גם מספר כרטיס מים וגם מספר מד, מה שיגרום לעדכון רק במצב ששורה במערכת קיימת עם שני הנתונים כמו שקיים בקובץ.        </Typography>
      </Box>

      {/* Selection Area (Centered) */}
      <Box sx={{ width: '100%', maxWidth: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Table Header */}
        <Box sx={{ 
          borderBottom: `1px solid ${isDarkMode ? '#2A364F' : '#E2E8F0'}`, 
          pb: 1, 
          mb: 2, 
          textAlign: 'center', 
          width: '220px',
          mx: 'auto'
        }}>
          <Typography sx={{ color: '#3B82F6', fontWeight: 600, fontSize: '14px', fontFamily: 'Rubik' }}>
            כותרת במערכת
          </Typography>
        </Box>

        {/* Column List (Centered under title) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 'fit-content' }}>
          {KEY_CANDIDATES.map((col) => {
            const isMapped = !!mappings[col.id]
            const isChecked = selectedKeyColumns.includes(col.id) || (isOnlyOneMapped && isMapped)
            
            return (
              <Box 
                key={col.id} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  direction: 'rtl',
                  position: 'relative',
                  opacity: isMapped ? 1 : 0.4,
                  height: '40px'
                }}
              >
                {/* Info Icon with Premium Tooltip */}
                <Tooltip 
                  title={`ערך מהקובץ: ${mappings[col.id] || 'אין נתונים'}`} 
                  arrow 
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#061F43A1',
                        color: '#F8FAFD',
                        fontSize: '13px',
                        fontFamily: 'Rubik',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        height: '30px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }
                    },
                    arrow: {
                      sx: {
                        color: '#061F43A1'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', zIndex: 2 }}>
                    <img src={MeterInfoIcon} alt="info" style={{ width: 16, height: 16 }} />
                  </Box>
                </Tooltip>

                {/* Checkbox (Middle in RTL) */}
                <Checkbox
                  checked={isChecked}
                  onChange={() => !isOnlyOneMapped && onKeyColumnToggle(col.id)}
                  disabled={isOnlyOneMapped && isMapped}
                  sx={{
                    color: '#3B82F6',
                    zIndex: 2,
                    p: 0.5,
                    '&.Mui-checked': { color: '#3B82F6' },
                    '&.Mui-disabled': { color: '#3B82F6' }
                  }}
                />

                {/* Label (Leftmost in RTL) */}
                <Typography 
                  sx={{ 
                    color: TEXT_PRIMARY, 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    fontFamily: 'Rubik',
                    zIndex: 2,
                    minWidth: '120px',
                    textAlign: 'right'
                  }}
                >
                  {col.label}
                </Typography>

                {/* Strike-through Line - spans across visible elements */}
                {!isMapped && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      left: 15,
                      right: 15,
                      height: '1px',
                      bgcolor: TEXT_PRIMARY,
                      top: '50%',
                      zIndex: 1,
                      pointerEvents: 'none'
                    }} 
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Explanatory Message at the far right below the selection area */}
      {isOnlyOneMapped && (
        <Box sx={{ textAlign: 'right', width: '100%', px: 1, mt: 1 }}>
          <Typography sx={{ color: TEXT_PRIMARY, fontSize: '14px', fontFamily: 'Rubik', direction: 'rtl' }}>
            עמודת '{missingColumnLabel}' לא קיימת בשלב מיפוי העמודות ולכן העדכון יתבצע לפי עמודת '{activeColumnLabel}'.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
